// ChatMessageList.jsx
import React, {
    useMemo,
    useRef,
    useState,
    useEffect,
    useCallback,
} from "react";
import ChatMessage from "./ChatMessage";
import "./ChatMessageList.css";

const SCROLL_THRESHOLD_PX = 120;

const ChatMessageList = ({ mensagens, onResponderMensagem }) => {
    const mensagensMap = useMemo(() => {
        const map = {};
        (mensagens || []).forEach((m) => {
            const key = m?.logConversasWhatsAppCodigoExterno;
            if (key) map[key] = m;
        });
        return map;
    }, [mensagens]);

    // ✅ este é o elemento que realmente terá overflow-y:auto
    const listRef = useRef(null);

    // refs / flags
    const messageRefs = useRef({});
    const shouldAutoScrollRef = useRef(true);
    const isAutoScrollingRef = useRef(false);
    const pendingScrollRef = useRef(false);
    const roScheduledRef = useRef(false);

    // UI
    const [highlightedId, setHighlightedId] = useState(null);
    const highlightTimeoutRef = useRef(null);

    // ✅ botão
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);

    const getMensagemRespondida = (mensagem) => {
        const idResp = mensagem?.logConversasWhatsAppIdMensagemRespondida;
        if (!idResp) return null;
        return mensagensMap[idResp] || null;
    };

    const registerMessageRef = useCallback(
        (codigoExterno) => (el) => {
            if (!codigoExterno) return;
            if (el) messageRefs.current[codigoExterno] = el;
            else delete messageRefs.current[codigoExterno];
        },
        [],
    );

    // ✅ recalcula se está no fim e mostra/esconde botão
    const updateScrollState = useCallback(() => {
        const el = listRef.current;
        if (!el) return;

        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        const isNearBottom = distanceFromBottom < SCROLL_THRESHOLD_PX;

        shouldAutoScrollRef.current = isNearBottom;
        setShowScrollToBottom(!isNearBottom);
    }, []);

    const handleScrollToMensagem = useCallback(
        (mensagemAlvo) => {
            if (!mensagemAlvo) return;

            const id = mensagemAlvo?.logConversasWhatsAppCodigoExterno;
            if (!id) return;

            const el = messageRefs.current[id];
            if (!el) return;

            isAutoScrollingRef.current = true;
            el.scrollIntoView({ behavior: "smooth", block: "center" });

            setTimeout(() => {
                isAutoScrollingRef.current = false;
                updateScrollState();
            }, 450);

            setHighlightedId(id);
            if (highlightTimeoutRef.current)
                clearTimeout(highlightTimeoutRef.current);

            highlightTimeoutRef.current = setTimeout(() => {
                setHighlightedId(null);
                highlightTimeoutRef.current = null;
            }, 2500);
        },
        [updateScrollState],
    );

    const handleScroll = useCallback(() => {
        if (isAutoScrollingRef.current) return;
        updateScrollState();
    }, [updateScrollState]);

    const scrollToBottom = useCallback((smooth = true) => {
        const el = listRef.current;
        if (!el) return;

        if (pendingScrollRef.current) return;
        pendingScrollRef.current = true;
        isAutoScrollingRef.current = true;

        const behavior = smooth ? "smooth" : "auto";
        const doScroll = () => el.scrollTo({ top: el.scrollHeight, behavior });

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                doScroll();
                setTimeout(() => {
                    doScroll();

                    isAutoScrollingRef.current = false;
                    pendingScrollRef.current = false;

                    shouldAutoScrollRef.current = true;
                    setShowScrollToBottom(false);
                }, 140);
            });
        });
    }, []);

    // ✅ estado inicial do botão
    useEffect(() => {
        const t = setTimeout(() => updateScrollState(), 0);
        return () => clearTimeout(t);
    }, [updateScrollState]);

    // ✅ quando mensagens mudarem, recalcula e auto-scroll se estava no fim
    useEffect(() => {
        updateScrollState();
        if (!shouldAutoScrollRef.current) return;
        scrollToBottom(false); // no primeiro layout costuma ser melhor "auto"
    }, [mensagens?.length, updateScrollState, scrollToBottom]);

    // ✅ corrige “para no meio” (mídia/layout mudando)
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;

        const ro = new ResizeObserver(() => {
            updateScrollState();
            if (!shouldAutoScrollRef.current) return;

            if (roScheduledRef.current) return;
            roScheduledRef.current = true;

            requestAnimationFrame(() => {
                roScheduledRef.current = false;

                isAutoScrollingRef.current = true;
                el.scrollTop = el.scrollHeight;

                setTimeout(() => {
                    el.scrollTop = el.scrollHeight;
                    isAutoScrollingRef.current = false;
                    setShowScrollToBottom(false);
                }, 80);
            });
        });

        ro.observe(el);
        return () => ro.disconnect();
    }, [updateScrollState]);

    return (
        <div className="cml-root">
            {/* ✅ wrapper flex com min-height:0 ajuda o overflow funcionar em flex */}
            <div className="cml-wrapper">
                <div className="cml-list" ref={listRef} onScroll={handleScroll}>
                    {(mensagens || []).map((mensagem) => {
                        const mensagemRespondida = getMensagemRespondida(mensagem);
                        const codigoExterno =
                            mensagem?.logConversasWhatsAppCodigoExterno ||
                            `fallback-${mensagem?.logConversasWhatsAppId}`;

                        return (
                            <ChatMessage
                                key={codigoExterno}
                                mensagem={mensagem}
                                mensagemRespondida={mensagemRespondida}
                                onResponderMensagem={onResponderMensagem}
                                onScrollToMensagem={handleScrollToMensagem}
                                highlighted={
                                    highlightedId === mensagem?.logConversasWhatsAppCodigoExterno
                                }
                                refCallback={registerMessageRef(
                                    mensagem?.logConversasWhatsAppCodigoExterno,
                                )}
                            />
                        );
                    })}
                </div>

                {showScrollToBottom && (
                    <button
                        type="button"
                        className="cml-scroll-bottom-btn"
                        onClick={() => scrollToBottom(true)}
                        title="Ir para o final"
                        aria-label="Ir para o final"
                    >
                        ⬇
                    </button>
                )}
            </div>
        </div>
    );
};

export default ChatMessageList;
